// src/components/NamaFormConfig.jsx
import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Typography, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Button, Snackbar, Alert,
    CircularProgress, Menu, MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from '@mui/icons-material/Close';

import { database } from "../../../../firebase";
import { ref, push, set, onValue, update, remove } from "firebase/database";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function NamaFormConfig() {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [fieldValue, setFieldValue] = useState("");
    const [indexValue, setIndexValue] = useState("");

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const fileInputRef = useRef(null);
    const title = "Daftar Nama Form";
    const dbPath = "db_config/db_observation/tb_nama_form";
    const fieldName = "nama_form";
    const primaryKeyField = "id_form";

    // Ambil data dari Firebase
    useEffect(() => {
        const tbRef = ref(database, dbPath);
        setLoading(true);

        const unsubscribe = onValue(tbRef, (snapshot) => {
            const data = snapshot.val();
            let rawDataList = [];
            if (snapshot.exists()) {
                rawDataList = Object.keys(data).map(key => ({
                    [primaryKeyField]: key,
                    ...data[key]
                }));
            }

            setDataList(rawDataList);
            setLoading(false);
        }, (error) => {
            console.error("Error:", error);
            setSnackbar({
                open: true,
                message: `Gagal memuat data ${title.toLowerCase()}.`,
                severity: "error",
            });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [dbPath]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = () => {
        setIsEditing(false);
        setFieldValue("");
        setIndexValue("");
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFieldValue("");
        setIndexValue("");
        setIsEditing(false);
        setCurrentId(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleIndexChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        setIndexValue(value);
    };

    const handleInputChange = (e) => {
        setFieldValue(e.target.value);
    };

    const handleSave = async () => {
        if (!fieldValue.trim()) {
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} tidak boleh kosong.`,
                severity: "warning",
            });
            return;
        }

        // Validasi duplikat
        const normalizedFieldValue = fieldValue.toLowerCase().trim();
        const isDuplicate = dataList.some(item =>
            item[fieldName]?.toLowerCase().trim() === normalizedFieldValue &&
            item[primaryKeyField] !== currentId
        );

        if (isDuplicate) {
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} "${fieldValue}" sudah ada.`,
                severity: "error",
            });
            return;
        }

        try {
            let dataToSave = {
                [fieldName]: fieldValue,
            };

            if (indexValue) {
                dataToSave.index = indexValue;
            }

            if (isEditing) {
                const itemRef = ref(database, `${dbPath}/${currentId}`);
                await update(itemRef, dataToSave);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil diperbarui.`,
                    severity: "success",
                });
            } else {
                const tbRef = ref(database, dbPath);
                const newRef = push(tbRef);
                const key = newRef.key;
                const finalData = {
                    ...dataToSave,
                    [primaryKeyField]: key,
                };
                await set(newRef, finalData);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil disimpan.`,
                    severity: "success",
                });
            }
            handleCloseDialog();
        } catch (error) {
            console.error("Gagal simpan/update:", error);
            setSnackbar({
                open: true,
                message: `Gagal menyimpan/memperbarui data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const tbRef = ref(database, dbPath);
                const snapshot = await onValuePromise(tbRef);
                const existingData = snapshot.exists() ? snapshot.val() : {};
                const existingFieldValues = Object.values(existingData).map(item => item[fieldName]?.toLowerCase().trim());
                
                let importedCount = 0;
                let ignoredCount = 0;
                
                for (const item of jsonData) {
                    let importedValue = item["Nama Form"];
                    if (!importedValue) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const valueLower = importedValue.toLowerCase().trim();
                    if (existingFieldValues.includes(valueLower)) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const newRef = push(tbRef);
                    const key = newRef.key;
                    const dataToSave = {
                        [primaryKeyField]: key,
                        [fieldName]: importedValue,
                    };
                    await set(newRef, dataToSave);
                    importedCount++;
                }
                
                setSnackbar({
                    open: true,
                    message: `Berhasil impor ${importedCount} data baru. ${ignoredCount} data sudah ada/invalid diabaikan.`,
                    severity: "success",
                });
            } catch (error) {
                console.error("Gagal impor Excel:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal mengimpor data dari Excel.",
                    severity: "error",
                });
            } finally {
                event.target.value = null;
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const exportToExcel = () => {
        if (!dataList || dataList.length === 0) {
            setSnackbar({ 
                open: true, 
                message: "Tidak ada data untuk diekspor.", 
                severity: "warning" 
            });
            return;
        }

        const formattedData = dataList.map((item) => ({
            [primaryKeyField]: item[primaryKeyField],
            ["Index"]: item.index,
            ["Nama Form"]: item[fieldName]
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Nama Form");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "Data_Nama_Form.xlsx");
    };

    const handleMenuClick = (event, row) => {
        setMenuAnchor(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedRow(null);
    };

    const handleEdit = (row) => {
        handleMenuClose();
        setIsEditing(true);
        setCurrentId(row[primaryKeyField]);
        setFieldValue(row[fieldName]);
        setIndexValue(row.index || "");
        setOpenDialog(true);
    };

    const handleDelete = (row) => {
        handleMenuClose();
        setSelectedRow(row);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedRow) return;
        
        try {
            const idToDelete = selectedRow[primaryKeyField];
            const itemRef = ref(database, `${dbPath}/${idToDelete}`);
            await remove(itemRef);
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} berhasil dihapus.`,
                severity: "success",
            });
            setOpenDeleteDialog(false);
            setSelectedRow(null);
        } catch (error) {
            console.error("Gagal hapus:", error);
            setSnackbar({
                open: true,
                message: `Gagal menghapus data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRow(null);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'inherit' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">{title}</Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton color="primary" size="small" onClick={handleOpenDialog}>
                        <AddIcon />
                    </IconButton>
                    <IconButton color="primary" size="small" onClick={handleImportButtonClick}>
                        <FileUploadOutlinedIcon />
                    </IconButton>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImportExcel}
                    />
                    <IconButton color="primary" size="small" onClick={exportToExcel}>
                        <FileDownloadOutlinedIcon />
                    </IconButton>
                </Stack>
            </Stack>

            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Aksi</TableCell>
                            <TableCell>Id</TableCell>
                            <TableCell>Index</TableCell>
                            <TableCell>Nama Form</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataList.length > 0 ? (
                            dataList
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row[primaryKeyField]}>
                                        <TableCell>
                                            <IconButton size="small" onClick={(event) => handleMenuClick(event, row)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{row[primaryKeyField]}</TableCell>
                                        <TableCell>{row.index || "-"}</TableCell>
                                        <TableCell>{row[fieldName]}</TableCell>
                                    </TableRow>
                                ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    Tidak ada data {title.slice(title.indexOf(" ") + 1).toLowerCase()}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleEdit(selectedRow)}>
                    <EditIcon fontSize="small" sx={{ mr: 2 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={() => handleDelete(selectedRow)}>
                    <DeleteIcon fontSize="small" sx={{ mr: 2 }} />
                    Hapus
                </MenuItem>
            </Menu>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <Typography variant="body2">
                    {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, dataList.length)} of ${dataList.length}`}
                </Typography>
                <TablePagination
                    component="div"
                    count={dataList.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                    labelRowsPerPage=""
                    labelDisplayedRows={() => ""}
                    sx={{
                        ".MuiTablePagination-toolbar": {
                            padding: 0,
                            minHeight: "auto",
                        },
                        ".MuiTablePagination-actions": {
                            display: "block",
                        },
                        ".MuiTablePagination-selectLabel": {
                            display: "none",
                        },
                        ".MuiTablePagination-displayedRows": {
                            display: "none",
                        },
                        ".MuiTablePagination-select": {
                            marginLeft: "8px",
                            marginRight: "8px",
                        },
                    }}
                />
            </Box>

            {/* Dialog Tambah/Edit */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    {isEditing ? `Edit ${title.slice(title.indexOf(" ") + 1)}` : `Tambah ${title.slice(title.indexOf(" ") + 1)}`}
                    <IconButton aria-label="close" onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        margin="normal"
                        label="Index"
                        type="number"
                        value={indexValue}
                        onChange={handleIndexChange}
                        fullWidth
                        sx={{ mb: 2 }}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                        }}
                    />
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Nama Form"
                        type="text"
                        fullWidth
                        value={fieldValue}
                        onChange={handleInputChange}
                    />
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDialog}>Batal</Button>
                    <Button onClick={handleSave} variant="contained">
                        {isEditing ? "Update" : "Simpan"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Konfirmasi Hapus */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Konfirmasi Hapus</DialogTitle>
                <DialogContent>
                    <Typography>
                        Apakah Anda yakin ingin menghapus {title.slice(title.indexOf(" ") + 1).toLowerCase()} "{selectedRow?.[fieldName]}"?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDeleteDialog}>Batal</Button>
                    <Button onClick={confirmDelete} variant="contained" color="error">
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default NamaFormConfig;